import React from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { MdCheck, MdClose } from "react-icons/md";
import SiteLayout from "components/layout/SiteLayout";
import theme from "theme";
import { brandAssets, pricing } from "content/site";
import {
	Container,
	MarketingCard,
	PrimaryButton,
	SecondaryButton,
	Section,
	SectionLead,
	SectionTitle,
} from "components/marketing/primitives";

/** name + tagline + price + description + body (features + CTA) */
const PLAN_CARD_ROWS = 5;

const HeroSection = styled(Section)`
	padding-top: clamp(3rem, 6vw, 4.5rem);
`;

const PlanGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: 1.25rem;

	@media (min-width: 700px) {
		grid-template-columns: repeat(2, 1fr);
	}

	@media (min-width: 1100px) {
		grid-template-columns: repeat(4, 1fr);
	}
`;

const PlanCard = styled(MarketingCard)`
	display: flex;
	flex-direction: column;
	gap: 0.65rem;

	@media (min-width: 700px) {
		display: grid;
		grid-template-rows: subgrid;
		grid-row: span ${PLAN_CARD_ROWS};
		row-gap: 0.65rem;
	}
`;

const PlanName = styled.h3`
	margin: 0;
	font-family: ${theme.fontFamily.sans};
	font-size: 1.25rem;
	font-weight: 800;
	color: ${theme.color.dark};
`;

const PlanTagline = styled.p`
	margin: 0;
	font-family: ${theme.fontFamily.sans};
	font-size: 0.875rem;
	font-weight: 600;
	color: ${theme.color.primary};
	line-height: 1.3;
`;

const PlanPrice = styled.p`
	margin: 0;
	font-family: ${theme.fontFamily.sans};
	font-size: 2.25rem;
	font-weight: 800;
	color: ${theme.color.dark};
	line-height: 1.1;
`;

const PlanInterval = styled.span`
	font-size: 1rem;
	font-weight: 600;
	color: ${theme.color.greyD2};
`;

const PlanDescription = styled.p`
	margin: 0;
	font-family: ${theme.fontFamily.sans};
	font-size: 0.9375rem;
	color: ${theme.color.greyD2};
	line-height: 1.55;
`;

const PlanBody = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	height: 100%;
`;

const FeatureList = styled.ul`
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 0.4rem;
`;

const FeatureItem = styled.li`
	display: flex;
	align-items: flex-start;
	gap: 0.5rem;
	font-family: ${theme.fontFamily.sans};
	font-size: 0.875rem;
	line-height: 1.35;
	color: ${(p) => {
		if (p.$empty) return "transparent";
		return p.$included ? theme.color.dark : theme.color.grey;
	}};
`;

const FeatureIcon = styled.span`
	display: inline-flex;
	flex: 0 0 16px;
	width: 16px;
	height: 16px;
	margin-top: 0.05em;
	align-items: center;
	justify-content: center;
	line-height: 0;

	svg {
		width: 16px;
		height: 16px;
		flex-shrink: 0;
	}
`;

const FeatureText = styled.span`
	flex: 1 1 auto;
	min-width: 0;
`;

const PlanCta = styled.div`
	margin-top: auto;
	padding-top: 0.75rem;
`;

const ComparisonWrap = styled.div`
	overflow-x: auto;
	border: 1px solid ${theme.color.border};
	border-radius: ${theme.radius.lg};
	background: ${theme.color.surface};
	box-shadow: ${theme.boxShadow.card};
`;

const ComparisonTable = styled.table`
	width: 100%;
	min-width: 640px;
	border-collapse: collapse;
	font-family: ${theme.fontFamily.sans};
	font-size: 0.875rem;
`;

const Th = styled.th`
	text-align: center;
	padding: 0.85rem 0.75rem;
	border-bottom: 1px solid ${theme.color.border};
	font-weight: 700;
	color: ${theme.color.dark};
	&:first-child {
		text-align: left;
	}
`;

const Td = styled.td`
	padding: 0.75rem;
	border-bottom: 1px solid ${theme.color.border};
	text-align: center;
	color: ${theme.color.greyD2};
	&:first-child {
		text-align: left;
		font-weight: 600;
		color: ${theme.color.dark};
	}
	tr:last-child & {
		border-bottom: none;
	}
`;

const ExplainerGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
	gap: 1.25rem;
`;

const ExplainerCard = styled(MarketingCard)``;

const ExplainerTitle = styled.h3`
	margin: 0 0 0.5rem;
	font-family: ${theme.fontFamily.sans};
	font-size: 1.0625rem;
	font-weight: 800;
	color: ${theme.color.dark};
`;

const ExplainerBody = styled.p`
	margin: 0;
	font-family: ${theme.fontFamily.sans};
	font-size: 0.9375rem;
	color: ${theme.color.greyD2};
	line-height: 1.6;
`;

const Footnote = styled.p`
	margin: 1rem 0 0;
	font-family: ${theme.fontFamily.sans};
	font-size: 0.8125rem;
	color: ${theme.color.grey};
	line-height: 1.5;
`;

function renderPrice(plan) {
	if (plan.priceDisplay) {
		return <PlanPrice>{plan.priceDisplay}</PlanPrice>;
	}
	return (
		<PlanPrice>
			${plan.price}
			{plan.interval ? <PlanInterval>/{plan.interval}</PlanInterval> : null}
		</PlanPrice>
	);
}

function renderComparisonCell(value) {
	if (typeof value === "boolean") {
		return value ? (
			<MdCheck size={18} color={theme.color.primary} aria-label="Included" />
		) : (
			<MdClose size={18} color={theme.color.grey} aria-label="Not included" />
		);
	}
	return value;
}

function padFeatures(plan, slotCount) {
	const slots = [];
	for (let i = 0; i < slotCount; i += 1) {
		const feature = plan.features[i];
		if (feature == null) {
			slots.push({ feature: null, included: false, empty: true });
		} else {
			slots.push({ feature, included: Boolean(plan.included[i]), empty: false });
		}
	}
	return slots;
}

export default function PricingPage() {
	const pageTitle = `${pricing.title} — Truss Security`;
	const featureSlotCount = Math.max(...pricing.plans.map((plan) => plan.features.length));

	return (
		<>
			<Helmet>
				<title>{pageTitle}</title>
				<meta name="description" content={pricing.lead} />
				<meta property="og:title" content={pageTitle} />
				<meta property="og:description" content={pricing.lead} />
				<link rel="icon" href={brandAssets.favicon} />
			</Helmet>
			<SiteLayout>
				<HeroSection>
					<Container>
						<SectionTitle>{pricing.title}</SectionTitle>
						<SectionLead>{pricing.lead}</SectionLead>
					</Container>
				</HeroSection>

				<Section $alt>
					<Container>
						<PlanGrid>
							{pricing.plans.map((plan) => (
								<PlanCard key={plan.name}>
									<PlanName>{plan.name}</PlanName>
									<PlanTagline>{plan.tagline}</PlanTagline>
									{renderPrice(plan)}
									<PlanDescription>{plan.description}</PlanDescription>
									<PlanBody>
										<FeatureList>
											{padFeatures(plan, featureSlotCount).map((slot, index) => (
												<FeatureItem
													key={`${plan.name}-feature-${index}`}
													$included={slot.included}
													$empty={slot.empty}
													aria-hidden={slot.empty || undefined}
												>
													{slot.empty ? null : (
														<>
															<FeatureIcon aria-hidden>
																{slot.included ? (
																	<MdCheck size={16} color={theme.color.primary} />
																) : (
																	<MdClose size={16} color={theme.color.grey} />
																)}
															</FeatureIcon>
															<FeatureText>{slot.feature}</FeatureText>
														</>
													)}
												</FeatureItem>
											))}
										</FeatureList>
										<PlanCta>
											{plan.cta.variant === "primary" ? (
												<PrimaryButton href={plan.cta.href} style={{ width: "100%", textAlign: "center" }}>
													{plan.cta.label}
												</PrimaryButton>
											) : (
												<SecondaryButton href={plan.cta.href} style={{ width: "100%", textAlign: "center" }}>
													{plan.cta.label}
												</SecondaryButton>
											)}
										</PlanCta>
									</PlanBody>
								</PlanCard>
							))}
						</PlanGrid>
					</Container>
				</Section>

				<Section>
					<Container>
						<SectionTitle as="h2">{pricing.comparison.title}</SectionTitle>
						<ComparisonWrap>
							<ComparisonTable>
								<thead>
									<tr>
										<Th>Feature</Th>
										<Th>Community</Th>
										<Th>Access</Th>
										<Th>Deployment</Th>
										<Th>Enterprise</Th>
									</tr>
								</thead>
								<tbody>
									{pricing.comparison.rows.map((row) => (
										<tr key={row.feature}>
											<Td>{row.feature}</Td>
											<Td>{renderComparisonCell(row.community)}</Td>
											<Td>{renderComparisonCell(row.access)}</Td>
											<Td>{renderComparisonCell(row.deployment)}</Td>
											<Td>{renderComparisonCell(row.enterprise)}</Td>
										</tr>
									))}
								</tbody>
							</ComparisonTable>
						</ComparisonWrap>
						<Footnote>{pricing.footnote}</Footnote>
					</Container>
				</Section>

				<Section $alt>
					<Container>
						<ExplainerGrid>
							{pricing.explainers.map((item) => (
								<ExplainerCard key={item.title}>
									<ExplainerTitle>{item.title}</ExplainerTitle>
									<ExplainerBody>{item.body}</ExplainerBody>
								</ExplainerCard>
							))}
						</ExplainerGrid>
					</Container>
				</Section>
			</SiteLayout>
		</>
	);
}
